import React from 'react';
import Debug from 'debug';
import shallowCompare from '../../lib/shallowCompare';
import SuggestItem from './SuggestItem';
import styles from './index.sass';
const debug = Debug('delectable');

class AddressSearch extends React.Component {
  static defaultProps = {
    fixtures: [],
    initialValue: '',
    placeholder: 'Search places',
    disabled: false,
    className: '',
    inputClassName: '',
    location: null,
    radius: null,
    bounds: null,
    country: null,
    types: null,
    googleMaps: null,
    maxSuggests: 4,
    onSuggestSelect: () => {},
    onFocus: () => {},
    onBlur: () => {},
    onChange: () => {},
    onChangeSuggests: () => {},
    skipSuggest: () => {},
    autoActivateFirstSuggest: false,
    autoFocus: false
  };

  constructor(props) {
    super(props);
    this._isMounted = false;
    this.state = {
      isSuggestsHidden: true,
      userInput: this.props.initialValue,
      activeSuggest: null,
      suggests: []
    };
  }

  componentWillReceiveProps(nextProps) {
    if(this.props.initialValue !== nextProps.initialValue) {
      this.setState({userInput : nextProps.initialValue});
    }
  }

  componentDidMount() {
    this.setInputValue(this.props.initialValue);
    let googleMaps = this.props.googleMaps || (window.google && window.google.maps) || this.googleMaps;
    if(!googleMaps) {
      debug('Google map api was not found in the page.');
      return;
    }
    this.googleMaps = googleMaps;
    this.autocompleteService = new googleMaps.places.AutocompleteService();
    this.geocoder = new googleMaps.Geocoder();
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  setInputValue(value) {
    this.setState({userInput : value});
  }

  handleOnInputChange() {
    let userInput = this.refs.geosuggestInput.value;

    this.setState({userInput: userInput}, () => {
      this.showSuggests();
      this.props.onChange(userInput);
    });
  }

  handleOnFocus() {
    this.showSuggests();
    this.props.onFocus();
  }

  update(value) {
    this.setState({userInput: value});
    this.props.onChange(value);
  }

  clear() {
    this.setState({userInput: ''}, () => this.hideSuggests());
  }

  searchSuggests() {
    if(!this.state.userInput) {
      this.updateSuggests();
      return;
    }

    let options = {
      input: this.state.userInput
    };

    if(this.props.location) {
      options.location = this.props.location;
    }

    if(this.props.radius) {
      options.radius = this.props.radius;
    }

    if(this.props.bounds) {
      options.bounds = this.props.bounds;
    }

    if(this.props.types) {
      options.types = this.props.types;
    }

    if(this.props.country) {
      options.componentRestrictions = {
        country: this.props.country
      };
    }

    this.autocompleteService.getPlacePredictions(
      options,
      suggestsGoogle => {
        this.updateSuggests(suggestsGoogle);

        if(this.props.autoActivateFirstSuggest) {
          this.activateSuggest('next');
        }
      }
    );
  }

  updateSuggests(suggestsGoogle) {
    if(!suggestsGoogle) {
      suggestsGoogle = [];
    }

    let suggests = [];
    let regex = new RegExp(this.state.userInput, 'gim');
    let skipSuggest = this.props.skipSuggest;

    this.props.fixtures.forEach(suggest => {
      if(!skipSuggest(suggest) && suggest.label.match(regex)) {
        suggest.placeId = suggest.label;
        suggests.push(suggest);
      }
    });

    suggestsGoogle.slice(0, this.props.maxSuggests).forEach(suggest => {
      if(!skipSuggest(suggest)) {
        suggests.push({
          label: suggest.description,
          placeId: suggest.place_id
        });
      }
    });

    this.props.onChangeSuggests(suggests.length);

    this.setState({suggests: suggests});
  }

  showSuggests() {
    this.searchSuggests();
    this.setState({isSuggestsHidden: false});
  }

  hideSuggests() {
    this.props.onBlur();
    setTimeout(() => {
      if(this._isMounted) {
        this.setState({isSuggestsHidden: true});
        this.props.onChangeSuggests(0);
      }
    }, 100);
  }

  handleOnInputKeyDown(e) {
    switch (e.which) {
      case 40: // DOWN
        e.preventDefault();
        this.activateSuggest('next');
        break;
      case 38: // UP
        e.preventDefault();
        this.activateSuggest('prev');
        break;
      case 13: // ENTER
        e.preventDefault();
        this.selectSuggest(this.state.activeSuggest);
        break;
      case 9: // TAB
        this.selectSuggest(this.state.activeSuggest);
        break;
      case 27: // ESC
        this.hideSuggests();
        break;
      default:
        break;
    }
  }

  activateSuggest(direction) { // eslint-disable-line complexity
    if(this.state.isSuggestsHidden) {
      this.showSuggests();
      return;
    }

    let suggestsCount = this.state.suggests.length - 1;
    let next = direction === 'next';
    let newActiveSuggest = null;
    let newIndex = 0;
    let i = 0;

    for(i; i <= suggestsCount; i++) {
      if(this.state.suggests[i] === this.state.activeSuggest) {
        newIndex = next ? i + 1 : i - 1;
      }
    }

    if(!this.state.activeSuggest) {
      newIndex = next ? 0 : suggestsCount;
    }

    if(newIndex >= 0 && newIndex <= suggestsCount) {
      newActiveSuggest = this.state.suggests[newIndex];
    }

    this.setState({activeSuggest: newActiveSuggest});
  }

  selectSuggest(suggest) {
    if(!suggest) {
      suggest = {
        label: this.state.userInput
      };
    }

    this.setState({
      isSuggestsHidden: true,
      userInput: ''
    });

    if(suggest.location) {
      this.props.onSuggestSelect(suggest);
      return;
    }

    this.geocodeSuggest(suggest);

    this.props.onChangeSuggests(0);
  }

  geocodeSuggest(suggest) {
    this.geocoder.geocode(
      {address: suggest.label},
      (results, status) => {
        if(status !== this.googleMaps.GeocoderStatus.OK) {
          return;
        }

        let gmaps = results[0];
        let location = gmaps.geometry.location;

        suggest.gmaps = gmaps;
        suggest.location = {
          lat: location.lat(),
          lng: location.lng()
        };

        this.props.onSuggestSelect(suggest);
      }
    );
  }

  getSuggestItems() {
    return this.state.suggests.map((suggest) => {
      let isActive = this.state.activeSuggest && suggest.placeId === this.state.activeSuggest.placeId;

      return (
        <SuggestItem
          key={suggest.placeId}
          suggest={suggest}
          isActive={isActive}
          onSuggestSelect={this.selectSuggest.bind(this)} />
      );
    });
  }

  getSuggestsClasses() {
    let classes = styles.addressSearchSuggests;
    classes += (this.state.isSuggestsHidden ? (' ' + styles.addressSearchSuggestsHidden) : '');
    return classes;
  }

  render() {
    return (
      <div className={styles.addressSearch + ' ' + this.props.className}>
        <input
          className={styles.addressSearchInput + ' ' + this.props.inputClassName}
          ref="geosuggestInput"
          type="text"
          autoFocus={this.props.autoFocus}
          autoComplete="off"
          value={this.state.userInput}
          placeholder={this.props.placeholder}
          disabled={this.props.disabled}
          onKeyDown={this.handleOnInputKeyDown.bind(this)}
          onChange={this.handleOnInputChange.bind(this)}
          onFocus={this.handleOnFocus.bind(this)} />
        <ul className={this.getSuggestsClasses()}>
          {this.getSuggestItems()}
        </ul>
      </div>
    );
  }
}

export default AddressSearch;



// WEBPACK FOOTER //
// banquet/components/AddressSearch/index.js